import { AxiosError, AxiosRequestConfig } from "axios";

import { AuroraV2Abilities } from "@/features/auroraV2/aurora-V2.types";
import { BatteryRecommendation, BatterySizerResponse, BatterySizerSettings } from "@/features/battery-sizer";
import {
  ConsumptionEstimates,
  ConsumptionProfile,
  ConsumptionProfileInput,
  EstimateConsumptionInput,
  Tariff,
  Utility,
} from "@/types/consumption-profile";
import { BatteryPurpose } from "@/types/proposal-data";

import {
  Annotation,
  CalculatePricingPayload,
  EnerfloProposalData,
  EnerfloProposalTemplate,
  FileAttachment,
  OptimizeDesignAction,
  PricingOperation,
  ProposalPdfInfo,
  SelectOption,
  ShareFormPayload,
  ShareFormResult,
  UnsavedAnnotation,
} from "../../types";
import { VariablesStore } from "../app-variables/with-app-variables";
import { makeDeleteRequest, makeGetRequest, makePostRequest, makePutRequest } from "./helpers";

// Change the URL to point to our App instead of legacy API for demo requests
const createApiUrl = (url: string) => url.replace("api/proposals/demo/", "proposals/api/demo/");

// Use this when we have to go directly to V2
const createV2ApiUrl = (path: string) => {
  const v2ApiHost = VariablesStore.getVariables().V2_DOMAIN;
  if (!v2ApiHost) return path;

  return `https://api.${v2ApiHost}${path}`;
};

export const fetchTemplate = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<EnerfloProposalTemplate>(createApiUrl(`/api/proposals/${reportHash}/template`), options);

export const fetchData = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<EnerfloProposalData>(createApiUrl(`/api/proposals/${reportHash}/data`), options);

export const fetchPdfInfo = async (reportHash: string) =>
  makeGetRequest<ProposalPdfInfo>(createApiUrl(`/api/proposals/${reportHash}/pdf-info`));

export const syncData = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<EnerfloProposalData>(createApiUrl(`/api/proposals/${reportHash}/sync`), options);

export const createFileAttachmentUrl = (reportHash: string, file: FileAttachment) => {
  if (file.id) {
    // this would always be coming from v2
    return createV2ApiUrl(`/api/proposals/${reportHash}/signedUrl?fileId=${file.id}`);
  }
  return file.externalUrl;
};

export const postDesignOptimization = async (
  reportHash: string,
  payload: OptimizeDesignAction["data"],
  options?: AxiosRequestConfig
) =>
  makePostRequest<OptimizeDesignAction["data"], EnerfloProposalData>(
    createApiUrl(`/api/proposals/${reportHash}/optimize`),
    payload,
    options
  );

export const postApplyForFinancing = async (reportHash: string, options?: AxiosRequestConfig) =>
  makePostRequest<{}, { redirectUrl: string }>(createApiUrl(`/api/proposals/${reportHash}/apply`), {}, options);

export const getAuroraV2Abilities = async (proposalId: string) =>
  makeGetRequest<{ abilities: AuroraV2Abilities; externalDesignToolLink: string }>(
    `/api/proposals/${proposalId}/aurora/abilities`
  );

export const postPricingCalculation = async <
  TOperation extends PricingOperation,
  TData = CalculatePricingPayload<TOperation>,
>(
  reportHash: string,
  operation: TOperation,
  data: TData,
  options?: AxiosRequestConfig
) =>
  makePostRequest<TData, EnerfloProposalData>(createApiUrl(`/api/proposals/${reportHash}/${operation}`), data, options);

interface FetchAnnotationsResult {
  annotations: Annotation[];
}

export const fetchAnnotations = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<FetchAnnotationsResult>(createApiUrl(`/api/proposals/${reportHash}/annotations`), options);

interface PostAnnotationResult {
  success: boolean;
  status: string;
  annotation: Annotation;
}

export const postAnnotation = async (reportHash: string, payload: UnsavedAnnotation, options?: AxiosRequestConfig) =>
  makePostRequest<UnsavedAnnotation, PostAnnotationResult>(
    createApiUrl(`/api/proposals/${reportHash}/annotation`),
    payload,
    options
  );

interface DeleteAnnotationResult {
  success: boolean;
  status: string;
  annotation: Annotation;
}

export const deleteAnnotation = async (reportHash: string, annotationId: number, options?: AxiosRequestConfig) =>
  makeDeleteRequest<{ screen: string }, DeleteAnnotationResult>(
    createApiUrl(`/api/proposals/${reportHash}/annotation/${annotationId}`),
    options
  );

export const getRateScheduleOptions = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<{ options: SelectOption[] }>(
    createApiUrl(`/api/proposals/${reportHash}/rate-schedule-options`),
    options
  );

export const postShareForm = async (reportHash: string, payload: ShareFormPayload, options?: AxiosRequestConfig) =>
  makePostRequest<ShareFormPayload, ShareFormResult>(
    createApiUrl(`/api/proposals/${reportHash}/share`),
    payload,
    options
  );

export enum IntegratedPluginStatus {
  Up = "UP",
  Down = "DOWN",
}

export const getSharePreview = (reportHash: string, message: string, options?: AxiosRequestConfig) =>
  makeGetRequest<string>(createApiUrl(`/api/proposals/${reportHash}/share/preview`), {
    ...options,
    params: { message },
  });

export type AerialyticDesignToolMetadata = {
  aerialyticLeadId: string;
  aerialyticProposalId: string;
  designToolRequestId: string;
  designToolDesignId: string;
  previouslyFinalized: boolean;
};

export type AuroraDesignToolMetadata = {
  externalDesignToolLink?: string;
  abilities?: {
    canViewExternalDesign: boolean;
    canViewExternalProposal: boolean;
  };
  designModeLink?: string;
  salesModeLink?: string;
};

export type SolargrafDesignToolMetadata = {
  designLink?: string;
};

export type ScaniflyDesignToolMetadata = {
  embedLink: string;
  externalDesignToolLink: string;
  abilities: {
    canSyncDesign: boolean;
    canViewExternalDesign: boolean;
  };
};

type DesignToolMetadataBase = {
  integratedPluginStatus?: IntegratedPluginStatus;
};

export type DesignToolMetadata = DesignToolMetadataBase &
  (
    | {
        tool: "aerialytic";
        data: AerialyticDesignToolMetadata;
      }
    | {
        tool: "AuroraDTP";
        data: AuroraDesignToolMetadata;
      }
    | {
        tool: "solargraf";
        data: SolargrafDesignToolMetadata;
      }
    | {
        tool: "scanifly";
        data: ScaniflyDesignToolMetadata;
      }
  );

export const fetchDesignToolData = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<DesignToolMetadata>(createApiUrl(`/api/proposals/design-tool/${reportHash}/data`), options);

export const getDesignToolPluginStatus = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<{ integratedPluginStatus?: IntegratedPluginStatus }>(
    createApiUrl(`/api/proposals/design-tool/${reportHash}/plugin-status`),
    options
  );

export const createFileUpload = (reportHash: string, payload: any, options?: AxiosRequestConfig) =>
  makePostRequest(createApiUrl(`/api/proposals/design-tool/${reportHash}/layout-image`), payload, options);

export const postSyncSolo = async (reportHash: string, options?: AxiosRequestConfig) =>
  makePostRequest<{}, EnerfloProposalData>(createApiUrl(`/api/proposals/${reportHash}/solo/sync`), {}, options);

export const postSyncAuroraV2Design = async (reportHash: string, options?: AxiosRequestConfig) =>
  makePostRequest<{}, EnerfloProposalData>(createApiUrl(`/api/proposals/${reportHash}/aurora/sync`), {}, options);

export interface ParsedApiError {
  statusCode?: number;
  message: string;
  code?: string;
}

export function parseApiError(error: AxiosError<any>): ParsedApiError {
  const { response } = error;

  const msg = response?.data?.error ?? response?.data?.message ?? response?.statusText ?? "Something went wrong";

  return {
    statusCode: response?.status,
    message: msg,
    code: response?.data?.code || "There was an error message",
  };
}

export const getSoloProposalUrl = async (reportHash: string, options?: AxiosRequestConfig) =>
  makeGetRequest<string>(createApiUrl(`/api/proposals/${reportHash}/solo/iframe-url`), options);

export const getConsumptionProfileById = async (dealId: string, profileId: string, options?: AxiosRequestConfig) =>
  makeGetRequest<{ result: ConsumptionProfile; status: string }>(
    createApiUrl(`/api/deals/${dealId}/consumption/profiles/${profileId}`),
    options
  );

export const getUtilityProvidersList = async (dealId: string, options?: AxiosRequestConfig) =>
  makeGetRequest<{ results: Utility[]; count: number; status: string }>(
    createApiUrl(`/api/deals/${dealId}/utility-options`),
    options
  );

export const getTariffsList = async (
  dealId: string,
  utilityId: string,
  postSolar: boolean,
  allowClosed: boolean,
  options?: AxiosRequestConfig
) =>
  makeGetRequest<{ results: Tariff[]; count: number; status: string }>(
    createApiUrl(
      `/api/deals/${dealId}/tariff-options?utilityId=${utilityId}&postSolar=${postSolar}&allowClosed=${allowClosed}`
    ),
    options
  );

export const postConsumptionEstimates = async (
  dealId: string,
  payload: EstimateConsumptionInput,
  options?: AxiosRequestConfig
) =>
  makePostRequest<EstimateConsumptionInput, { result: ConsumptionEstimates; status: string }>(
    createApiUrl(`/api/deals/${dealId}/consumption/calculate`),
    payload,
    options
  );

export const putUpdateConsumptionProfile = async (
  dealId: string,
  consumptionProfileId: string,
  payload: ConsumptionProfileInput,
  options?: AxiosRequestConfig
) =>
  makePutRequest<ConsumptionProfileInput, { result: ConsumptionProfile; status: string }>(
    createApiUrl(`/api/deals/${dealId}/consumption/profiles/${consumptionProfileId}`),
    payload,
    options
  );

export const postCreateConsumptionProfile = async (
  dealId: string,
  payload: ConsumptionProfileInput,
  options?: AxiosRequestConfig
) =>
  makePostRequest<ConsumptionProfileInput, { result: ConsumptionProfile; status: string }>(
    createApiUrl(`/api/deals/${dealId}/consumption/profiles`),
    payload,
    options
  );

// Fetch the list of recommendations for the provided settings parameters
export const postBatterySizerRecommendations = async (
  reportHash: string,
  payload: BatterySizerSettings,
  options?: AxiosRequestConfig
) =>
  makePostRequest<BatterySizerSettings, BatterySizerResponse>(
    createApiUrl(`/api/proposals/${reportHash}/battery-recommendations`),
    payload,
    options
  );

type UpdateBatterySizerPayload = {
  attachment: BatteryPurpose;
  hours: { start: number; end: number };
  daily: {
    hour: number;
    consumption: number;
    production: number;
  }[];
  batterySet: {
    orgEquipmentId: string;
    quantity: number;
  }[];
};

// Update the quantity for a single recommendation
export const postUpdateBatterySizerRecommendation = async (
  reportHash: string,
  payload: UpdateBatterySizerPayload,
  options?: AxiosRequestConfig
) =>
  makePostRequest<UpdateBatterySizerPayload, { recommendation: BatteryRecommendation }>(
    createApiUrl(`/api/proposals/${reportHash}/battery-recommendations/update-quantity`),
    payload,
    options
  );
